import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { projectTeamApi } from '../api/api';
import type { InvitedUser } from '../types/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import Navigation from '@/components/home/Navigation';
import Section from '@/components/home/Section';
import { STYLES } from '@/components/home/constants';

export const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [invitedUser, setInvitedUser] = useState<InvitedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setIsLoading(false);
        return;
      }

      try {
        const data = await projectTeamApi.getInvitationByToken(token);
        setInvitedUser(data.invited_user);
      } catch (err: any) {
        console.error('Error loading invitation:', err);
        setError(err.response?.data?.error || 'Failed to load invitation');
      } finally {
        setIsLoading(false);
      }
    };

    loadInvitation();
  }, [token]);

  const handleAcceptInvitation = async () => {
    if (!token) return;

    setIsAccepting(true);
    setError(null);

    try {
      await projectTeamApi.acceptInvitation(token);
      setSuccess(true);
      
      // Redirect to signup/login after 2 seconds
      setTimeout(() => {
        navigate('/signup');
      }, 2000);
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setError(err.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleNavigate = (sectionId: string) => {
    // Navigate to home page with section
    navigate(`/#${sectionId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-maintenance-navy flex flex-col">
        <div className="p-2 md:p-5">
          <Navigation onNavigate={handleNavigate} />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-white mx-auto mb-4" />
            <p className="text-white text-lg">Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-maintenance-navy flex flex-col">
        <div className="p-2 md:p-5">
          <Navigation onNavigate={handleNavigate} />
        </div>
        <Section
          id="invitation-success"
          backgroundImage="/static/images/sf2_1600.png"
          showBackToTop={false}
          overlayClassName={STYLES.overlay.heroGradient}
        >
          <div className="max-w-2xl mx-auto text-center py-12">
            <div className={`${STYLES.card} rounded-2xl p-8 md:p-12`}>
              <div className="mx-auto mb-6 w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-green-400" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Invitation Accepted!
              </h1>
              <p className="text-gray-300 text-lg mb-6">
                Welcome to {invitedUser?.project?.name}. Redirecting you to sign up...
              </p>
              <Loader2 className="h-6 w-6 animate-spin text-white mx-auto" />
            </div>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-maintenance-navy flex flex-col">
      <div className="p-2 md:p-5">
        <Navigation onNavigate={handleNavigate} />
      </div>
      
      <Section
        id="accept-invitation"
        backgroundImage="/static/images/sf2_1600.png"
        showBackToTop={false}
        overlayClassName={STYLES.overlay.heroGradient}
      >
        <div className="max-w-3xl mx-auto py-12">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {invitedUser && (
            <div className={`${STYLES.card} rounded-2xl p-8 md:p-12`}>
              <div className="text-center mb-8">
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
                  You've Been Invited!
                </h1>
                <p className="text-lg md:text-xl text-gray-300">
                  Join the{' '}
                  <span className="font-semibold text-white">
                    {invitedUser.project?.name}
                  </span>{' '}
                  project
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-6 mb-8 border border-white/20">
                <div className="space-y-4 text-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 font-medium">Project:</span>
                    <span className="font-semibold text-white text-right">{invitedUser.project?.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 font-medium">Client:</span>
                    <span className="font-semibold text-white text-right">{invitedUser.client?.name}</span>
                  </div>
                  {invitedUser.role && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 font-medium">Role:</span>
                      <span className="font-semibold text-white text-right">{invitedUser.role}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 font-medium">Email:</span>
                    <span className="font-semibold text-white text-right break-all">{invitedUser.email}</span>
                  </div>
                </div>

                {(invitedUser.can_access_asbuilds ||
                  invitedUser.can_access_progress ||
                  invitedUser.can_access_drones ||
                  invitedUser.can_access_drone_photos ||
                  invitedUser.can_access_surveillance) && (
                  <div className="mt-6 pt-6 border-t border-white/20">
                    <p className="text-gray-400 font-medium mb-4">Access Permissions:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {invitedUser.can_access_asbuilds && (
                        <div className="flex items-center text-white">
                          <CheckCircle2 className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                          <span>Asbuilds</span>
                        </div>
                      )}
                      {invitedUser.can_access_progress && (
                        <div className="flex items-center text-white">
                          <CheckCircle2 className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                          <span>Progress</span>
                        </div>
                      )}
                      {invitedUser.can_access_drones && (
                        <div className="flex items-center text-white">
                          <CheckCircle2 className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                          <span>Drone Videos</span>
                        </div>
                      )}
                      {invitedUser.can_access_drone_photos && (
                        <div className="flex items-center text-white">
                          <CheckCircle2 className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                          <span>Drone Photos</span>
                        </div>
                      )}
                      {invitedUser.can_access_surveillance && (
                        <div className="flex items-center text-white">
                          <CheckCircle2 className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                          <span>Surveillance</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleAcceptInvitation}
                disabled={isAccepting}
                className={`w-full px-6 py-4 rounded-md text-lg font-semibold transition-colors ${STYLES.button.primary}`}
              >
                {isAccepting ? (
                  <>
                    <Loader2 className="inline-block mr-2 h-5 w-5 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  'Accept Invitation'
                )}
              </button>

              <p className="text-center text-gray-400 text-sm mt-6">
                By accepting, you'll be able to create an account and access the project resources.
              </p>
            </div>
          )}
        </div>
      </Section>

      {/* Footer */}
      <div className="bg-maintenance-navy/95 py-4 text-center">
        <p className="text-gray-400 text-sm">
          &copy; 2026 Ironview 360. All rights reserved.
        </p>
      </div>
    </div>
  );
};

